(function (root) {
  "use strict";

  const EARTH_RADIUS_KM = 6371;
  const DAY_MS = 24 * 60 * 60 * 1000;
  const WEEKDAYS = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  function toFiniteNumber(value, fallback) {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function toRadians(value) {
    return (value * Math.PI) / 180;
  }

  function getDistanceKm(lat1, lng1, lat2, lng2) {
    const firstLat = toFiniteNumber(lat1, null);
    const firstLng = toFiniteNumber(lng1, null);
    const secondLat = toFiniteNumber(lat2, null);
    const secondLng = toFiniteNumber(lng2, null);

    if (
      firstLat === null ||
      firstLng === null ||
      secondLat === null ||
      secondLng === null
    ) {
      return Number.POSITIVE_INFINITY;
    }

    const latitudeDelta = toRadians(secondLat - firstLat);
    const longitudeDelta = toRadians(secondLng - firstLng);
    const a =
      Math.sin(latitudeDelta / 2) ** 2 +
      Math.cos(toRadians(firstLat)) *
        Math.cos(toRadians(secondLat)) *
        Math.sin(longitudeDelta / 2) ** 2;

    return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function normalizeGid(value, resourceType) {
    const raw = String(value || "").trim();
    if (!raw) return "";
    if (raw.startsWith("gid://shopify/")) return raw;
    return `gid://shopify/${resourceType}/${raw}`;
  }

  function normalizeLocationGid(value) {
    return normalizeGid(value, "Location");
  }

  function normalizeVariantGid(value) {
    return normalizeGid(value, "ProductVariant");
  }

  function parseClosedWeekdays(value) {
    const values = Array.isArray(value) ? value : String(value || "").split(",");
    return new Set(
      values
        .map((item) => Number(item))
        .filter((item) => Number.isInteger(item) && item >= 0 && item <= 6),
    );
  }

  function getZonedParts(date, timeZone) {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      weekday: "short",
    });
    const values = {};

    formatter.formatToParts(date).forEach((part) => {
      if (part.type !== "literal") values[part.type] = part.value;
    });

    const weekdayIndex = {
      Sun: 0,
      Mon: 1,
      Tue: 2,
      Wed: 3,
      Thu: 4,
      Fri: 5,
      Sat: 6,
    }[values.weekday];

    return {
      year: Number(values.year),
      month: Number(values.month),
      day: Number(values.day),
      hour: Number(values.hour) % 24,
      minute: Number(values.minute),
      second: Number(values.second),
      weekday: weekdayIndex,
    };
  }

  function formatLocalDate(year, month, day) {
    return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }

  function parseClosedDates(value) {
    const blocked = new Set();
    const lines = Array.isArray(value) ? value : String(value || "").split(/[\n,;]/);
    const validDate = (value) => {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
      const date = new Date(value + "T12:00:00Z");
      return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
    };
    for (const item of lines) {
      const raw = String(item || "").trim();
      const match = raw.match(/^(\d{4}-\d{2}-\d{2})(?:\s+(?:to|through)\s+(\d{4}-\d{2}-\d{2}))?$/i);
      if (!match || !validDate(match[1])) continue;
      const end = match[2] && validDate(match[2]) ? match[2] : match[1];
      const startTime = Date.parse(match[1] + "T12:00:00Z");
      const endTime = Date.parse(end + "T12:00:00Z");
      if (endTime < startTime || endTime - startTime > 45 * DAY_MS) continue;
      for (let t = startTime; t <= endTime; t += DAY_MS) {
        blocked.add(new Date(t).toISOString().slice(0, 10));
      }
    }
    return blocked;
  }

  function findNextOpenDate(parts, closedWeekdays, closedDates, workingDays = 1) {
    const localDateUtc = Date.UTC(parts.year, parts.month - 1, parts.day);
    let remaining = Math.max(1, Math.round(toFiniteNumber(workingDays, 1)));
    for (let offset = 1; offset <= 366; offset += 1) {
      const candidate = new Date(localDateUtc + offset * DAY_MS);
      const weekday = candidate.getUTCDay();
      const date = formatLocalDate(
        candidate.getUTCFullYear(),
        candidate.getUTCMonth() + 1,
        candidate.getUTCDate(),
      );
      if (closedWeekdays.has(weekday) || (closedDates && closedDates.has(date))) continue;
      remaining -= 1;
      if (remaining === 0) {
        return {
          offset,
          weekday,
          weekdayName: WEEKDAYS[weekday],
          date,
        };
      }
    }
    return null;
  }

  function getDeliveryPromise(options) {
    const settings = options || {};
    const now = settings.now instanceof Date ? settings.now : new Date();
    const timeZone = settings.timeZone || "Asia/Dubai";
    const cutoffHour = Math.min(
      23,
      Math.max(0, toFiniteNumber(settings.cutoffHour, 16)),
    );
    const cutoffMinute = Math.min(
      59,
      Math.max(0, toFiniteNumber(settings.cutoffMinute, 0)),
    );
    const closedWeekdays = parseClosedWeekdays(settings.closedWeekdays ?? [0]);
    const closedDates = parseClosedDates(settings.closedDates || "");
    const parts = getZonedParts(now, timeZone);
    const currentMinutes = parts.hour * 60 + parts.minute + parts.second / 60;
    const cutoffMinutes = cutoffHour * 60 + cutoffMinute;
    const todayDate = formatLocalDate(parts.year, parts.month, parts.day);
    const todayClosed = closedWeekdays.has(parts.weekday) || closedDates.has(todayDate);

    if (!todayClosed && currentMinutes < cutoffMinutes) {
      return {
        key: "today",
        label: "Get it Today",
        deliveryDate: todayDate,
        serviceLevel: "same_day",
        minutesRemaining: Math.max(0, Math.ceil(cutoffMinutes - currentMinutes)),
        cutoffPassed: false,
        timeZone,
      };
    }

    const nextOpenDate = findNextOpenDate(parts, closedWeekdays, closedDates, 1);
    const isTomorrow = nextOpenDate && nextOpenDate.offset === 1;

    return {
      key: isTomorrow ? "tomorrow" : "weekday",
      label: isTomorrow
        ? "Get it Tomorrow"
        : `Get it ${nextOpenDate?.weekdayName || "Next Working Day"}`,
      deliveryDate: nextOpenDate?.date || todayDate,
      serviceLevel: "next_day",
      minutesRemaining: 0,
      cutoffPassed: !todayClosed,
      timeZone,
    };
  }

  function parseAreaOptions(value) {
    return String(value || "")
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [name, latitude, longitude] = line.split("|").map((item) => item.trim());
        const lat = toFiniteNumber(latitude, null);
        const lng = toFiniteNumber(longitude, null);

        if (!name || lat === null || lng === null) return null;
        if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;

        return { name, lat, lng };
      })
      .filter(Boolean);
  }

  function normalizeStores(stores) {
    return (Array.isArray(stores) ? stores : [])
      .map((store, index) => ({
        ...store,
        id: normalizeLocationGid(store.id || store.locationId),
        name: String(store.name || `Store ${index + 1}`).trim(),
        lat: toFiniteNumber(store.lat ?? store.latitude, null),
        lng: toFiniteNumber(store.lng ?? store.longitude, null),
        radiusKm: Math.max(0, toFiniteNumber(store.radiusKm ?? store.radius, 10)),
        active: store.active !== false && String(store.active) !== "false",
        priority: toFiniteNumber(store.priority, index),
      }))
      .filter(
        (store) =>
          store.active &&
          store.id &&
          store.lat !== null &&
          store.lng !== null,
      );
  }

  function findEligibleStores(customerLocation, stores) {
    const customerLat = toFiniteNumber(customerLocation?.lat, null);
    const customerLng = toFiniteNumber(customerLocation?.lng, null);
    if (customerLat === null || customerLng === null) return [];

    return normalizeStores(stores)
      .map((store) => ({
        ...store,
        distanceKm: getDistanceKm(customerLat, customerLng, store.lat, store.lng),
      }))
      .filter((store) => store.distanceKm <= store.radiusKm)
      .sort((first, second) => {
        if (first.distanceKm !== second.distanceKm) {
          return first.distanceKm - second.distanceKm;
        }
        return first.priority - second.priority;
      });
  }

  function getConnectionNodes(connection) {
    if (!connection) return [];
    if (Array.isArray(connection.nodes)) return connection.nodes;
    if (Array.isArray(connection.edges)) {
      return connection.edges.map((edge) => edge?.node).filter(Boolean);
    }
    return [];
  }

  function indexAvailability(graphqlData) {
    const variants = getConnectionNodes(graphqlData?.nodes ? { nodes: graphqlData.nodes } : graphqlData);
    const result = {};

    variants.forEach((variant) => {
      if (!variant?.id) return;
      const variantId = normalizeVariantGid(variant.id);
      result[variantId] = {};

      getConnectionNodes(variant.storeAvailability).forEach((availability) => {
        const locationId = normalizeLocationGid(availability?.location?.id);
        if (!locationId) return;

        result[variantId][locationId] = {
          quantity: Math.max(0, toFiniteNumber(availability.quantityAvailable, 0)),
          available: availability.available === true,
          locationName: availability.location?.name || "",
          pickUpTime: availability.pickUpTime || "",
        };
      });
    });

    return result;
  }

  function consolidateRequirements(requirements) {
    const consolidated = new Map();

    (Array.isArray(requirements) ? requirements : []).forEach((requirement) => {
      const variantId = normalizeVariantGid(
        requirement?.variantId ?? requirement?.id,
      );
      const quantity = Math.max(1, toFiniteNumber(requirement?.quantity, 1));
      if (!variantId) return;
      consolidated.set(variantId, (consolidated.get(variantId) || 0) + quantity);
    });

    return Array.from(consolidated, ([variantId, quantity]) => ({
      variantId,
      quantity,
    }));
  }

  function findStoreForVariant(variantId, requiredQuantity, inventoryByVariant, stores) {
    const normalizedVariantId = normalizeVariantGid(variantId);
    const quantity = Math.max(1, toFiniteNumber(requiredQuantity, 1));
    const inventory = inventoryByVariant?.[normalizedVariantId] || {};

    return (
      (stores || []).find((store) => {
        const locationId = normalizeLocationGid(store.id || store.locationId);
        return (inventory[locationId]?.quantity || 0) >= quantity;
      }) || null
    );
  }

  function findCommonStore(requirements, inventoryByVariant, stores) {
    const requiredItems = consolidateRequirements(requirements);
    if (!requiredItems.length) return null;

    return (
      (stores || []).find((store) => {
        const locationId = normalizeLocationGid(store.id || store.locationId);
        return requiredItems.every((item) => {
          const inventory = inventoryByVariant?.[item.variantId]?.[locationId];
          return (inventory?.quantity || 0) >= item.quantity;
        });
      }) || null
    );
  }

  function getStandardDeliveryPromise(options) {
    const settings = options || {};
    const now = settings.now instanceof Date ? settings.now : new Date();
    const timezone = settings.timeZone || "Asia/Dubai";
    const parts = getZonedParts(now, timezone);
    const closedWeekdays = parseClosedWeekdays(settings.closedWeekdays ?? [0, 6]);
    const closedDates = parseClosedDates(settings.closedDates || "");
    const todayDate = formatLocalDate(parts.year, parts.month, parts.day);
    const todayClosed = closedWeekdays.has(parts.weekday) || closedDates.has(todayDate);
    const cutoffHour = Math.min(23, Math.max(0, toFiniteNumber(settings.cutoffHour, 12)));
    const cutoffMinute = Math.min(59, Math.max(0, toFiniteNumber(settings.cutoffMinute, 0)));
    const beforeCutoff = !todayClosed && parts.hour * 60 + parts.minute + parts.second / 60 < cutoffHour * 60 + cutoffMinute;
    const required = Math.max(1, Math.ceil(toFiniteNumber(settings.quantity, 1)));
    const warehouseQuantity = Math.max(0, toFiniteNumber(settings.warehouseQuantity, 0));
    const otherQuantity = Math.max(0, toFiniteNumber(settings.otherQuantity, 0));
    const fromWarehouse = warehouseQuantity >= required;
    if (!fromWarehouse && otherQuantity < required) return null;
    const warehouseFast = fromWarehouse && beforeCutoff;
    const branchDays = Math.min(10, Math.max(2, Math.ceil(toFiniteNumber(settings.branchDays, 2))));
    const branchAfterCutoff = !todayClosed && !beforeCutoff;
    const workingDays = fromWarehouse ? (warehouseFast ? 1 : 2) : branchDays + (branchAfterCutoff ? 1 : 0);
    const next = findNextOpenDate(parts, closedWeekdays, closedDates, workingDays);
    if (!next) return null;
    return {
      key: "standard",
      stockClass: fromWarehouse ? "warehouse" : "branch",
      warehouseFast,
      workingDays,
      deliveryDate: next.date,
      weekdayName: next.weekdayName,
      offset: next.offset,
      isTomorrow: next.offset === 1,
      cutoffPassed: !beforeCutoff,
      timeZone: timezone,
    };
  }

  const api = {
    consolidateRequirements,
    findCommonStore,
    findEligibleStores,
    findStoreForVariant,
    getDeliveryPromise,
    getStandardDeliveryPromise,
    getDistanceKm,
    indexAvailability,
    normalizeLocationGid,
    normalizeStores,
    normalizeVariantGid,
    parseAreaOptions,
  };

  root.ExpressDeliveryCore = api;
})(typeof window !== "undefined" ? window : globalThis);
